import type { FormEvent, ChangeEvent } from 'react'
import type { ClientType, SearchFormData, SingleSearch, BasicStatus } from 'types'

import { useState, useEffect, useRef } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import InputMask from 'react-input-mask'
import styled from 'styled-components'

import { useStore } from 'store'
import { dimmBackground, formatNIP, formatPhoneNumber, removeSpacesAndDashes } from 'utils'
import { clearURLQueryParam, getURLQueryParam, replaceURLParams } from 'utils/urlParams'
import copy from 'copy.json'
import { adminPanelPaths } from 'paths'
import { basicStatuses, STATUS } from 'common'
import usePrevious from 'hooks/usePrevious'

import Button from 'components/Button'
import Input, { PrefixedInput } from 'components/Input'
import InputLabel from 'components/InputLabel'
import Pagination from 'components/Pagination'
import SearchScreenNavigation from 'components/SearchScreenNavigation'
import Multiselect, { getSelectedValues, OptionProps } from 'components/Multiselect'
import Spinner from 'components/Spinner'
import Status from 'components/Status'
import Table from 'components/Table'
import PageContainer from 'components/PageContainer'
import Scroll from 'components/Scroll'

const texts_form = copy.common.form

const defaultSearchValues: SearchFormData = {
  statuses: [],
  company_name: null,
  tax_identifier: null,
  phone_number: null,
  contact_email: null,
  page_number: 0
}

type Props = {
  clientType: ClientType
}

export default function SearchScreen({ clientType }: Props) {
  const navigate = useNavigate()
  const { api, fieldErrors, clearFieldErrors } = useStore()
  const timeout = useRef(0)
  const previousClientType = usePrevious(clientType)
  const [searchClientData, setSearchClientData] = useState<SingleSearch[] | null>(null)
  const [currentPage, setCurrentPage] = useState(0)
  const [totalPages, setTotalPages] = useState(0)
  const [loading, setLoading] = useState(false)
  const [multiselectOptions, setMultiselectOptions] = useState<OptionProps[]>(
    basicStatuses.map(status => ({
      value: status,
      name: STATUS[status],
      isChecked: false
    }))
  )

  const page = getURLQueryParam('page')
  const previousPage = usePrevious(page)
  defaultSearchValues.page_number = page

  const [searchFormValues, setSearchFormValues] = useState(defaultSearchValues)
  const previousSearchFormValues = usePrevious(searchFormValues)

  const noResults = searchClientData && searchClientData.length === 0
  const tableIsVisible = searchClientData && searchClientData.length > 0
  const paginationIsVisible = tableIsVisible && totalPages > 1
  const spinnerIsVisible = loading && !searchClientData

  const handlePageChange = (currentStep: number) => {
    replaceURLParams(currentStep, 'page', navigate)
    setSearchFormValues(state => ({ ...state, page_number: currentStep - 1 }))
  }

  const handleSearch = (event: FormEvent<HTMLInputElement> | ChangeEvent<HTMLSelectElement>) => {
    clearFieldErrors(event.currentTarget.name)
    clearTimeout(timeout.current)
    clearURLQueryParam('page', navigate)

    const form = document.getElementById(`search-${clientType}-form`) as HTMLFormElement
    const values = Array.from(new FormData(form)).reduce((result, [name, value]) => {
      if (name === 'phone_number') {
        value = removeSpacesAndDashes(value as string)
      }
      return { ...result, [name]: value !== '' ? value : null }
    }, {} as SearchFormData)

    timeout.current = window.setTimeout(() => setSearchFormValues(values), 500)
  }

  const handleStatusChange = (options: OptionProps[]) => {
    setMultiselectOptions(options)
    setSearchFormValues(state => ({
      ...state,
      statuses: getSelectedValues(options) as BasicStatus[]
    }))
  }

  useEffect(() => {
    const differentClientType = previousClientType && previousClientType !== clientType
    const differentSearchValues = Object.keys(searchFormValues).some(
      key =>
        searchFormValues[key as keyof SearchFormData] !==
        previousSearchFormValues?.[key as keyof SearchFormData]
    )
    const differentPage = previousPage !== page

    if (differentClientType) {
      searchFormValues.page_number = page ? page : 0
    }

    if (differentPage) {
      searchFormValues.page_number = page
    }

    if (!differentClientType && !differentSearchValues && !differentPage) {
      return
    }

    setLoading(true)

    api
      .searchClients(clientType, {
        ...searchFormValues,
        page_number: searchFormValues.page_number
      })
      .then(response => {
        setLoading(false)

        if (response) {
          setCurrentPage(response.number + 1)
          setTotalPages(response.total_pages)
          setSearchClientData(response.content)
        }
      })
  }, [
    api,
    clientType,
    previousClientType,
    previousSearchFormValues,
    searchFormValues,
    page,
    previousPage
  ])

  useEffect(() => {
    dimmBackground()
    clearFieldErrors()
  }, [clearFieldErrors])

  return (
    <PageContainer>
      <Header>
        <h1>{copy.page.search.heading}</h1>
        <Link to={adminPanelPaths.registration} state={{ userType: clientType }}>
          {copy.page.search.link}
        </Link>
      </Header>

      <SearchScreenNavigation />

      <SearchForm id={`search-${clientType}-form`}>
        <InputLabel hideErrorOnFocus={false}>
          <span>{texts_form.status}</span>
          <Multiselect options={multiselectOptions} onSelectionChange={handleStatusChange} />
        </InputLabel>

        <InputLabel hideErrorOnFocus={false}>
          <span>{texts_form.company_name}</span>
          <span>{fieldErrors.company_name}</span>
          <Input name="company_name" onInput={handleSearch} />
        </InputLabel>

        <InputLabel hideErrorOnFocus={false}>
          <span>{texts_form.tax_identifier}</span>
          <span>{fieldErrors.tax_identifier}</span>
          <Input name="tax_identifier" onInput={handleSearch} />
        </InputLabel>

        <InputLabel hideErrorOnFocus={false}>
          <span>{texts_form.phone_number}</span>
          <span>{fieldErrors.phone_number}</span>
          <InputMask mask="999 999 999" maskPlaceholder={null}>
            <PrefixedInput name="phone_number" onInput={handleSearch} prefix="+48" type="tel" />
          </InputMask>
        </InputLabel>

        <InputLabel hideErrorOnFocus={false}>
          <span>{texts_form.email}</span>
          <span>{fieldErrors.contact_email}</span>
          <Input name="contact_email" onInput={handleSearch} />
        </InputLabel>

        <InputLabel hideErrorOnFocus={false}>
          <span>{texts_form.assigned_agent}</span>
          <span>{fieldErrors.assigned_agent}</span>
          <Input name="assigned_agent" onInput={handleSearch} />
        </InputLabel>
      </SearchForm>

      {noResults && <h2 style={{ margin: 'auto' }}>{copy.common.no_search_results}</h2>}
      {spinnerIsVisible && <Spinner style={{ margin: 'auto' }} />}

      {tableIsVisible && (
        <SortSection>
          <span>{copy.page.search.search_results}</span>

          {/* Comment this Select till backend is ready
        <label>
          Sortuj po
          <Select>
            <option>Data ostatniej modyfikacji</option>
          </Select>
        </label> */}
        </SortSection>
      )}

      {tableIsVisible && (
        <Scroll>
          <Table>
            <thead>
              <tr>
                <th>{texts_form.status}</th>
                <th>{texts_form.id}</th>
                <th>{texts_form.company_name}</th>
                <th>{texts_form.tax_identifier}</th>
                <th>{texts_form.phone_number}</th>
                <th>{texts_form.email}</th>
                <th>{texts_form.assigned_agent}</th>
                <th />
              </tr>
            </thead>

            <tbody>
              {searchClientData?.map(client => (
                <tr key={client.id}>
                  <td>
                    <Status statusType={client.status} />
                  </td>
                  <td>
                    <strong>{client.id}</strong>
                  </td>
                  <td>
                    <strong>{client.company_name}</strong>
                  </td>
                  <td>{formatNIP(client.tax_identifier)}</td>
                  <td>{formatPhoneNumber(client.phone_number)}</td>
                  <td>{client.contact_email}</td>
                  <td>{client.assigned_agent || '-'}</td>
                  <td>
                    <Button
                      small
                      onClick={() =>
                        navigate(`${client.id}`, { state: { rememberBackPage: true } })
                      }
                    >
                      {copy.common.button.choose}
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </Scroll>
      )}

      {paginationIsVisible && (
        <StyledPagination
          numberOfSteps={totalPages}
          currentStep={currentPage}
          onChange={handlePageChange}
        />
      )}
    </PageContainer>
  )
}
const Header = styled.header`
  display: flex;
  justify-content: space-between;
  align-items: center;

  > h1 {
    font: var(--600-24);
  }

  > a {
    padding: 8px 16px;
    border: 1px solid;
    border-radius: 4px;

    color: var(--green);
    font: var(--600-13);
    text-decoration: none;
    text-transform: uppercase;
  }
`

const SearchForm = styled.form`
  display: flex;
  flex-wrap: wrap;
  gap: 24px;

  padding: 24px;
  background: var(--very-light-blue);

  > ${InputLabel} {
    flex: 1;
  }
`

const SortSection = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;

  > span {
    font: var(--600-16);
  }

  > label {
    display: flex;
    align-items: center;
    gap: 8px;
  }
`

const StyledPagination = styled(Pagination)`
  align-self: center;
`
